import { useState, type ComponentType, type ReactNode } from "react";

import Button from "components/Button";
import Modal from "components/Modal";

/**
 * Presentational base for the "viewer is not the purchaser" handoff. Owns the
 * shared layout, the attestation modal, and the attestation error handling.
 * The single use case (continue on the purchaser's behalf — covering both
 * signing and payment) supplies its copy and attestation trigger via props.
 */
export default function HandoffPanel({
  icon: Icon,
  heading,
  body,
  primaryLabel,
  attestModalTitle,
  attestModalBody,
  attestConfirmLabel,
  isMutating,
  onAttest,
}: {
  icon: ComponentType<{ className?: string }>;
  heading: string;
  body: ReactNode;
  primaryLabel: string;
  attestModalTitle: string;
  attestModalBody: ReactNode;
  attestConfirmLabel: string;
  isMutating: boolean;
  onAttest: () => Promise<void>;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onAttest();
    } catch {
      setError("We couldn't record your confirmation. Please try again.");
    }
  };

  return (
    <div className="mx-auto max-w-2xl py-12 text-center">
      <Icon className="mx-auto h-16 w-16 text-sage" />
      <h2 className="mt-4 font-serif text-2xl italic text-evergreen">
        {heading}
      </h2>
      <p className="mt-3 text-soil">{body}</p>

      <div className="mt-8 flex justify-center">
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          {primaryLabel}
        </Button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={attestModalTitle}
        centerTitle
      >
        <p className="text-soil">{attestModalBody}</p>
        {error && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => setIsModalOpen(false)}
            disabled={isMutating}
            className="w-full sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isMutating}
            className="w-full sm:flex-1"
          >
            {isMutating ? "Confirming…" : attestConfirmLabel}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
